import { useCallback } from "react";
import { type SearchSettings, useSearchkitClient } from "@/hooks/use-searchkit-client";
import type { SearchWindow } from "@/lib/when-search-window";

// The programs search front door (`Programs::UseCases::FilterProgramSearch`)
// reads this block, builds the anytime-OR-session-intersects clause, then strips
// the key before forwarding to OpenSearch. Key matches WHEN_PAYLOAD_KEY.
const WHEN_PAYLOAD_KEY = "_travily_when";

// The same front door reads this: the selected family members' ages, so it
// can keep only programmes whose [min_age, max_age] suits the whole group. Stripped
// before forwarding, like the "When" payload.
const WHO_PAYLOAD_KEY = "_travily_who";

// Hoisted so `useSearchkitClient`'s memo deps stay reference-stable across
// renders. Per-page Searchkit config never depends on hook state.
const SEARCH_SETTINGS: SearchSettings = {
  // Searchkit reads `insideBoundingBox` from InstantSearch UI state (set by
  // `useGeoSearch().refine`) and emits an ES `geo_bounding_box` filter against
  // this field. Hits' `_source[locations]` is also surfaced as `_geoloc` for
  // InstantSearch geo widgets.
  geo_attribute: "locations",
  // Target Searchkick's `.word_middle` subfield — it indexes every substring
  // of every word for typo/prefix-friendly matching. The SearchableProgram
  // concern patches it with `search_analyzer: searchkick_word_search` so the
  // query side tokenises normally.
  search_attributes: [{ field: "name.word_middle", weight: 3 }, "tagline.word_middle", "description.word_middle"],
  result_attributes: [
    "id",
    "sqid",
    "name",
    "tagline",
    "min_age",
    "max_age",
    "instruction_languages",
    "influences",
    "community_program",
    "drop_off_available",
    "featured",
    "published_at",
    "created_at",
    "cover_image",
    "photos",
    "account",
    "locations",
  ],
  highlight_attributes: [],
  facet_attributes: [
    { attribute: "community_program", field: "community_program", type: "string" },
    { attribute: "drop_off_available", field: "drop_off_available", type: "string" },
    { attribute: "family_favorite", field: "family_favorite", type: "string" },
    { attribute: "featured", field: "featured", type: "string" },
    { attribute: "availability_mode", field: "availability_mode", type: "string" },
    { attribute: "instruction_languages", field: "instruction_languages", type: "string" },
    { attribute: "influences", field: "influences", type: "string" },
    { attribute: "format", field: "format", type: "string" },
  ],
  sorting: {
    default: { field: "_score", order: "desc" },
    _newest: { field: "created_at", order: "desc" },
  },
};

export function useProgramsSearch(whenWindow: SearchWindow | null, whoAges: number[] | null) {
  // Inject the "When" date window + "Who" ages onto the raw request body. The
  // transporter reads the latest enhancer via a ref, so the search client is
  // never rebuilt.
  const requestEnhancer = useCallback(
    (body: Record<string, unknown>) => {
      let next = body;
      if (whenWindow) next = { ...next, [WHEN_PAYLOAD_KEY]: whenWindow };
      if (whoAges && whoAges.length > 0) next = { ...next, [WHO_PAYLOAD_KEY]: { ages: whoAges } };
      return next;
    },
    [whenWindow, whoAges],
  );

  return useSearchkitClient({
    host: "/api/programs",
    indexName: "programs",
    searchSettings: SEARCH_SETTINGS,
    requestEnhancer,
    sessionKey: "programs",
  });
}
